export type MoveTarget = number | "top" | "bottom";

export interface ReorderResult {
  order: string[];
  leadingId: string | null;
  changed: boolean;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/**
 * Pure block-move: lift the selected ids out (preserving their relative order),
 * then re-insert them as one contiguous block, anchored on the lowest selected
 * position and offset by `target`. Single-select is a block of one. The gap
 * between non-contiguous selected items always closes on a move.
 */
export function computeReorder(
  order: string[],
  selectedIds: string[],
  target: MoveTarget,
): ReorderResult {
  const selected = new Set(selectedIds.filter((id) => order.includes(id)));
  if (selected.size === 0) {
    return { order: [...order], leadingId: null, changed: false };
  }

  const block = order.filter((id) => selected.has(id));
  const remaining = order.filter((id) => !selected.has(id));

  const lowestSelIndex = order.findIndex((id) => selected.has(id));
  // How many unselected items sit before the lowest selected item.
  const baseIndex = order.slice(0, lowestSelIndex).filter((id) => !selected.has(id)).length;

  let targetIndex: number;
  if (target === "top") {
    targetIndex = 0;
  } else if (target === "bottom") {
    targetIndex = remaining.length;
  } else {
    targetIndex = clamp(baseIndex + target, 0, remaining.length);
  }

  const newOrder = [...remaining.slice(0, targetIndex), ...block, ...remaining.slice(targetIndex)];

  const movingUp = target === "top" || (typeof target === "number" && target < 0);
  const leadingId = movingUp ? block[0] : block[block.length - 1];

  const changed = newOrder.some((id, i) => id !== order[i]);

  return { order: newOrder, leadingId, changed };
}
